import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api";

// Ward's outreach cheat sheet: who to email, what they last booked, what they've
// spent, a checkbox for "I sent it", a way to say "I'm not sending this one, and
// why", and a note for the team on either. The list is built by the server from
// the synced invoices. Replies are tracked in HubSpot, not here.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAILY_PACE = 20;
const TIER_SHORT = { active: "Active", lapsed: "Lapsed", dormant: "Dormant", cold: "Cold" };

// Internal only: Ward sees what a client has spent; the note never mentions money.
const money = (n) => `$${Math.round(n || 0).toLocaleString("en-US")}`;
const fmtDate = (d) => {
  if (!d) return "";
  const [y, m] = d.split("-");
  return `${MONTHS[Number(m) - 1]} ${y}`;
};
const isSent = (r) => ["sent", "replied"].includes(r.status);
// Ward chose not to write to them; his note says why, in his own words. A skip
// keeps them off the due list for good.
const isSkipped = (r) => r.status === "skip";
// A sent note that got a reply nobody has decided about yet: no deal, not dismissed.
const isToDealWith = (r) => isSent(r) && r.hs_replied && !r.deal_id && !r.dismissed_at;
const today = () => new Date().toISOString().slice(0, 10);

// What HubSpot said about a sent note, after "Check HubSpot for replies".
function ReplyBadge({ row }) {
  if (!isSent(row) || !row.hs_checked_at) return null;
  if (row.hs_replied) {
    const d = row.hs_replied_at ? new Date(row.hs_replied_at) : null;
    return <span className="mk-or-reply yes">Replied{d ? ` ${MONTHS[d.getMonth()]} ${d.getDate()}` : ""}</span>;
  }
  if (row.hs_in_crm === false) return <span className="mk-or-reply warn">Not in HubSpot</span>;
  if (!row.hs_logged) return <span className="mk-or-reply warn">Email not logged in HubSpot</span>;
  return <span className="mk-or-reply">No reply yet</span>;
}

// The client's reply, as HubSpot gave it. Without email-read permission HubSpot
// sends a redaction notice instead of the text, so say that and link out.
function Reply({ row, onDeal, onDismiss }) {
  const [busy, setBusy] = useState(false);
  if (!row.hs_replied) return null;
  const d = row.hs_replied_at ? new Date(row.hs_replied_at) : null;
  // One request at a time: the buttons stay off until the answer is back, so a
  // double-press can't ask for two deals.
  const run = async (fn) => {
    setBusy(true);
    try { await fn(row); } finally { setBusy(false); }
  };
  return (
    <div className="mk-or-replybox" data-testid={`mk-or-reply-${row.email}`}>
      <div className="mk-label">
        Their reply{d ? ` · ${MONTHS[d.getMonth()]} ${d.getDate()}` : ""}
        {row.hs_reply_subject ? ` · ${row.hs_reply_subject}` : ""}
      </div>
      {row.hs_reply_snippet
        ? <div className="mk-or-notebody">{row.hs_reply_snippet}</div>
        : <div className="mk-or-dim">HubSpot logged the reply but didn't share the text with the cockpit.
            Open it in HubSpot to read it.</div>}
      {row.hs_contact_url && (
        <a className="mk-inline-link" href={row.hs_contact_url} target="_blank" rel="noopener noreferrer">
          Open in HubSpot ↗</a>
      )}
      {row.deal_id ? (
        <div className="mk-b-row">
          <a className="mk-inline-link" href={row.deal_url} target="_blank" rel="noopener noreferrer"
            data-testid={`mk-or-deal-${row.email}`}>
            {row.deal_existing
              ? `Already a deal: ${row.deal_existing.name} (${row.deal_existing.stage}) ↗`
              : "Deal in HubSpot ↗"}</a>
        </div>
      ) : row.dismissed_at ? (
        <div className="mk-or-dim" data-testid={`mk-or-dismissed-${row.email}`}>Not a deal</div>
      ) : (
        <div className="mk-b-row">
          <button type="button" className="mk-btn" disabled={busy}
            onClick={() => run(onDeal)}
            data-testid={`mk-or-make-deal-${row.email}`}>
            {busy ? "Working…" : "Make it a deal"}</button>
          <button type="button" className="mk-btn-quiet" disabled={busy}
            onClick={() => run(onDismiss)}
            data-testid={`mk-or-not-deal-${row.email}`}>Not a deal</button>
        </div>
      )}
    </div>
  );
}

// The box that pops up under a row once Ward makes his call, or opens his note
// to edit it. "sent": the row is already marked, the note is optional. "skip":
// nothing is recorded until he says why, so every skip carries its reason.
// "edit": just the note.
const PANEL = {
  sent: { label: "Sent. Anything the team should know?", button: "Save note",
    placeholder: "Optional. Never sent to the client." },
  skip: { label: "Why aren't you sending this one?", button: "Mark as not sending",
    placeholder: "Describe the situation for the team. Never sent to the client." },
  edit: { label: "Notes for the team", button: "Save note",
    placeholder: "Anything the team should know. Never sent to the client." },
};

function NotePanel({ row, mode, onNotes, onSkip, onClose }) {
  const [draft, setDraft] = useState(row.notes || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const p = PANEL[mode];
  const needsText = mode === "skip" && !draft.trim();
  const submit = async () => {
    setBusy(true);
    setMsg("");
    const ok = draft === (row.notes || "") || await onNotes(row, draft);
    if (ok && mode === "skip") await onSkip(row);
    setBusy(false);
    if (ok) onClose();
    else setMsg("Couldn't save. Check your connection and try again.");
  };
  return (
    <div className={`mk-or-panel p-${mode}`} data-testid={`mk-or-panel-${row.email}`}>
      <div className="mk-label">{p.label}</div>
      <textarea className="mk-textarea" rows={3} value={draft} autoFocus placeholder={p.placeholder}
        onChange={(e) => setDraft(e.target.value)} data-testid={`mk-or-notes-${row.email}`} />
      {msg && <span className="mk-b-error">{msg}</span>}
      <div className="mk-b-row">
        <button type="button" className="mk-btn" disabled={busy || needsText} onClick={submit}
          data-testid={`mk-or-panel-save-${row.email}`}>{busy ? "Saving…" : p.button}</button>
        <button type="button" className="mk-btn-quiet" disabled={busy} onClick={onClose}>
          {mode === "skip" ? "Cancel" : "Close"}</button>
      </div>
    </div>
  );
}

function Row({ row, tiers, onToggle, onSkip, onUnskip, onNotes, onDeal, onDismiss, showReply }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [panel, setPanel] = useState(null);  // null | "sent" | "skip" | "edit"
  const sent = isSent(row);
  const skipped = isSkipped(row);
  // Picking the choice already made takes it back, so a mis-click is one click to undo.
  const pickSent = () => {
    if (sent) { onToggle(row); setPanel(null); return; }
    onToggle(row);
    setPanel("sent");
  };
  const pickSkip = () => {
    if (skipped) { onUnskip(row); setPanel(null); return; }
    setPanel("skip");
  };
  const mailto = `mailto:${row.email}?subject=${encodeURIComponent(row.note.subject)}&body=${encodeURIComponent(row.note.body)}`;
  const copy = () => {
    navigator.clipboard?.writeText(row.note.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={`mk-or-row ${sent ? "sent" : ""} ${isSkipped(row) ? "skip" : ""} ${row.dismissed_at && !row.deal_id ? "dismissed" : ""}`} data-testid={`mk-or-row-${row.email}`}>
      <div className="mk-or-line">
        <button type="button" className="mk-or-head" onClick={() => setOpen(!open)}>
          <span className="mk-or-who">
            <b>{row.company || row.email}</b>
            <span className="mk-or-dim">{row.email}</span>
          </span>
          <span className="mk-or-last">
            {fmtDate(row.last_booking)} · {money(row.last_amount)}
            {row.athlete_for_note ? ` · ${row.athlete_for_note}` : ""}
            <span className="mk-or-dim">
              <span className={`mk-or-tier t-${row.tier}`}>{TIER_SHORT[row.tier]}</span>
              {" "}{money(row.revenue)} lifetime · {row.bookings} booking{row.bookings === 1 ? "" : "s"}
            </span>
            <ReplyBadge row={row} />
          </span>
          <span className="mk-or-chev">{open ? "▴" : "▾"}</span>
        </button>
        <div className="mk-or-choice" role="group" aria-label="Did you send it?">
          <button type="button" className={`mk-or-pick p-sent ${sent ? "on" : ""}`} onClick={pickSent}
            title={sent ? "Marked as sent. Click to undo." : "I sent it"}
            data-testid={`mk-or-sent-${row.email}`}>✓ Sent</button>
          <button type="button" className={`mk-or-pick p-skip ${skipped || panel === "skip" ? "on" : ""}`}
            onClick={pickSkip} title={skipped ? "Marked as not sending. Click to undo." : "I'm not sending this one"}
            data-testid={`mk-or-skip-${row.email}`}>✕ Not sending</button>
        </div>
      </div>

      {panel ? (
        <NotePanel key={panel} row={row} mode={panel} onNotes={onNotes} onSkip={onSkip}
          onClose={() => setPanel(null)} />
      ) : row.notes ? (
        <button type="button" className="mk-or-notepeek" onClick={() => setPanel("edit")}
          title="Edit the note" data-testid={`mk-or-note-${row.email}`}>
          <span className="mk-label">{skipped ? "Not sending, because" : "Note"}</span>{row.notes}
        </button>
      ) : (sent || skipped) && (
        <button type="button" className="mk-or-addnote" onClick={() => setPanel("edit")}>+ Add a note</button>
      )}

      {showReply && !open && <div className="mk-or-body"><Reply row={row} onDeal={onDeal} onDismiss={onDismiss} /></div>}

      {open && (
        <div className="mk-or-body">
          <Reply row={row} onDeal={onDeal} onDismiss={onDismiss} />
          <div className="mk-or-facts">
            <div><span className="mk-label">History</span>
              {row.history.map((h) => `${fmtDate(h.date)} ${money(h.amount)}${h.athletes.length ? ` (${h.athletes.join(", ")})` : ""}`).join(" · ")}
            </div>
            {row.last_desc && (
              <div><span className="mk-label">Last invoice said</span>
                <span className="mk-or-dim">{row.last_desc}</span></div>
            )}
            <div><span className="mk-label">Usually books</span>{MONTHS[row.usual_month - 1]}
              {" · "}{tiers[row.tier]}</div>
          </div>
          <div className="mk-or-note">
            <div className="mk-or-dim">Subject: {row.note.subject}</div>
            <div className="mk-or-notebody">{row.note.body}</div>
          </div>
          <div className="mk-b-row">
            <a className="mk-btn" href={mailto} data-testid={`mk-or-mail-${row.email}`}>Open in email</a>
            <button type="button" className="mk-btn-quiet" onClick={copy}>{copied ? "Copied ✓" : "Copy note"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Outreach() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState("due");
  const [tier, setTier] = useState("");
  const [q, setQ] = useState("");
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState(null);

  // Asks HubSpot (through the cockpit) about every note ticked as sent, then reloads.
  const checkReplies = async () => {
    setChecking(true);
    setReport(null);
    try {
      const { data: r } = await api.post("/marketing/outreach/check-replies");
      setReport(r);
      load();
    } catch (e) {
      setReport({ error: e?.response?.data?.detail || "Couldn't reach HubSpot. Try again." });
    } finally {
      setChecking(false);
    }
  };

  const load = useCallback(() => {
    api.get("/marketing/outreach")
      .then(({ data: d }) => setData(d))
      .catch((e) => setError(e?.response?.data?.detail || "Couldn't load the outreach list"));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Optimistic: the row changes the moment Ward clicks; it reverts only if the save
  // fails. `shown` is what to show meanwhile, `body` what the server is sent.
  const save = async (row, body, shown) => {
    const patchRow = (fields) => setData((d) => ({
      ...d, rows: d.rows.map((r) => (r.email === row.email ? { ...r, ...fields } : r)),
    }));
    patchRow({ ...body, ...shown });
    try {
      const { data: st } = await api.patch(`/marketing/outreach/${encodeURIComponent(row.email)}`, body);
      patchRow(st);
      return true;
    } catch {
      patchRow(Object.fromEntries(Object.keys({ ...body, ...shown }).map((k) => [k, row[k]])));
      window.alert("Couldn't save that. Check your connection and try again.");
      return false;
    }
  };
  const now = () => new Date().toISOString();
  const toggle = (row) => (isSent(row)
    ? save(row, { status: "todo" })
    : save(row, { status: "sent" }, { sent_at: row.sent_at || now(), skipped_at: null }));
  const skip = (row) => save(row, { status: "skip" }, { skipped_at: now() });
  const unskip = (row) => save(row, { status: "todo" }, { skipped_at: null });
  // Not optimistic: undoing a failed note would wipe what Ward typed, so the box
  // keeps his text and says it didn't save.
  const saveNotes = async (row, notes) => {
    try {
      const { data: st } = await api.patch(`/marketing/outreach/${encodeURIComponent(row.email)}`, { notes });
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.email === row.email ? { ...r, ...st } : r)) }));
      return true;
    } catch {
      return false;
    }
  };

  // Both write one row's fields back into the list. A failure says so and leaves
  // the row as it was.
  const updateRow = (email, fields) => setData((d) => ({
    ...d, rows: d.rows.map((r) => (r.email === email ? { ...r, ...fields } : r)),
  }));
  const makeDeal = async (row) => {
    try {
      const { data: res } = await api.post(`/marketing/outreach/${encodeURIComponent(row.email)}/deal`);
      updateRow(row.email, { deal_id: res.deal_id, deal_url: res.deal_url, deal_existing: res.existing });
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't make the deal. Try again.");
    }
  };
  const dismiss = async (row) => {
    try {
      const { data: st } = await api.post(`/marketing/outreach/${encodeURIComponent(row.email)}/dismiss`);
      updateRow(row.email, { dismissed_at: st.dismissed_at });
    } catch {
      window.alert("Couldn't save that. Check your connection and try again.");
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (needle && !`${r.company} ${r.email} ${r.athlete_for_note}`.toLowerCase().includes(needle)) return false;
      if (tier && r.tier !== tier) return false;
      // Today's sends and skips stay on the due list so a mis-click is easy to undo.
      if (view === "due") return r.due && r.tier !== "cold"
        && (r.status === "todo" || (isSent(r) && (r.sent_at || "").slice(0, 10) === today())
          || (isSkipped(r) && (r.skipped_at || "").slice(0, 10) === today()));
      if (view === "done") return isSent(r);
      if (view === "skipped") return isSkipped(r);
      if (view === "replied") return isSent(r) && r.hs_replied;
      return true;
    }).sort((a, b) => (view === "replied"   // newest reply first
      ? (b.hs_replied_at || "").localeCompare(a.hs_replied_at || "") : 0));
  }, [data, view, tier, q]);

  if (error) return <div className="mk-info-empty">{error}</div>;
  if (!data) return <div className="mk-info-empty">Loading…</div>;
  const due = data.rows.filter((r) => r.due && r.tier !== "cold" && r.status === "todo").length;
  const sentToday = data.rows.filter((r) => isSent(r) && (r.sent_at || "").slice(0, 10) === today()).length;
  const sentTotal = data.rows.filter(isSent).length;
  // Counted here, not read from the server, so ticking "Sent" off lowers it too.
  const toDealWith = data.rows.filter(isToDealWith).length;

  return (
    <div className="mk-outreach" data-testid="mk-outreach">
      <p className="mk-b-intro">
        Who's due a note, what they last booked and what they've spent. Click a name to see the
        note and send it from your email, then press Sent. Not sending one? Press Not sending and
        say why. Either way you can leave a note for the team. About {DAILY_PACE} a day.
      </p>

      <div className="mk-or-stats">
        <div><b>{due}</b><span>left to send</span></div>
        <div><b>{sentToday}</b><span>sent today</span></div>
        <div><b>{sentTotal}</b><span>sent total</span></div>
        <div><b>{data.rows.filter((r) => isSent(r) && r.hs_replied).length}</b><span>replied</span></div>
        <div><b>{data.rows.filter(isSkipped).length}</b><span>not sending</span></div>
      </div>

      {/* Always visible: hiding it on the Sent view made it easy to miss. */}
      {(
        <div className="mk-or-hs">
          <button type="button" className="mk-btn" disabled={checking} onClick={checkReplies}
            data-testid="mk-or-check-replies">
            {checking ? "Checking HubSpot…" : "Check HubSpot for replies"}
          </button>
          {report && (report.error
            ? <span className="mk-b-error">{report.error}</span>
            : <span className="mk-or-dim">
                Checked {report.checked}: {report.replied} replied, {report.logged} emails logged in HubSpot
                {report.not_in_hubspot ? `, ${report.not_in_hubspot} not in HubSpot` : ""}
                {report.errors ? `, ${report.errors} couldn't be checked` : ""}.
              </span>)}
        </div>
      )}


      <div className="mk-b-row">
        {[["due", "Due now"], ["all", "Everyone"], ["done", "Sent"],
          ["replied", toDealWith > 0 ? `Replied (${toDealWith} new)` : "Replied"],
          ["skipped", "Not sending"]].map(([k, l]) => (
          <button key={k} type="button" className={`mk-btn-quiet ${view === k ? "on" : ""}`}
            onClick={() => setView(k)} data-testid={`mk-or-view-${k}`}>{l}</button>
        ))}
        <select className="mk-select" value={tier} onChange={(e) => setTier(e.target.value)}>
          <option value="">All tiers</option>
          {Object.entries(data.tiers).map(([k, l]) => <option key={k} value={k}>{l} · {data.counts[k]}</option>)}
        </select>
        <input className="mk-input mk-or-search" placeholder="Search company, email or athlete"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="mk-or-list">
        {rows.length === 0
          ? <div className="mk-info-empty">Nobody here right now.</div>
          : rows.map((r, i) => (
            <div key={r.email}>
              {/* Rows arrive due-first, newest tier first, biggest spend first. */}
              {!["all", "replied"].includes(view) && (i === 0 || rows[i - 1].tier !== r.tier) && (
                <div className="mk-or-group">{data.tiers[r.tier]}
                  <span> · {rows.filter((x) => x.tier === r.tier).length}</span></div>
              )}
              <Row row={r} tiers={data.tiers} onToggle={toggle} onSkip={skip} onUnskip={unskip}
                onNotes={saveNotes} onDeal={makeDeal}
                onDismiss={dismiss} showReply={view === "replied"} />
            </div>
          ))}
      </div>
      <div className="mk-b-dim">Built from invoices synced {data.syncedAt ? new Date(data.syncedAt).toLocaleString() : "not yet"}.
        "Usually books" uses invoice dates, which can trail the event by a few weeks.</div>
    </div>
  );
}
